import { Div, A } from '@codesuma/baseline'
import { CatalogCountry, CatalogOccasion } from '../../services/occasion-catalog'
import { toJalaali } from '../../services/jalaali'
import { J_MONTHS } from '../../configs/names'
import { toFaDigits } from '../../helpers/digits'
import { CONTACT_EMAIL } from '../../configs/app'
import style from './index.module.css'

// What this list is worth trusting, said once at the bottom rather than on every
// row: where the data came from, when someone last read those sources, and a way
// to say it is wrong.
//
// Not per-occasion links. Those would put a line under every row in an already
// dense list, for something a reader wants once and a maintainer wants in the
// data — which is where they already are, on every entry in data/occasions. The
// hosts are deduplicated here instead, because one ministry usually answers for
// a dozen days.
//
// The verification date is the part that actually signals whether to trust the
// page, so it is written out rather than buried.

const host = (url: string) => {
    try {
        return new URL(url).hostname.replace(/^www\./, '')
    } catch {
        return url
    }
}

const faJalaali = (iso: string) => {
    const [gy, gm, gd] = iso.split('-').map(Number)
    const { jy, jm, jd } = toJalaali(gy, gm, gd)
    return `${toFaDigits(jd)} ${J_MONTHS[jm - 1]} ${toFaDigits(jy)}`
}

export const Sources = (country: CatalogCountry, occasions: CatalogOccasion[]) => {
    const base = Div()
    base.addClass(style.sources)

    const disclaimer = Div('این فهرست از متن قوانین و صفحه‌های رسمی گرفته شده و ممکن است کامل یا به‌روز نباشد. پیش از برنامه‌ریزی، با منبع رسمی بررسی کنید.')
    disclaimer.addClass(style.disclaimer)
    base.append(disclaimer)

    // The country's own source, its regions' and its occasions', by host.
    const urls = [
        country.sourceUrl,
        ...country.regions.map(r => r.sourceUrl),
        ...occasions.map(o => o.sourceUrl),
        ...occasions.map(o => o.applicability?.sourceUrl),
    ].filter((u): u is string => !!u)

    const byHost = new Map<string, string>()
    for (const url of urls) if (!byHost.has(host(url))) byHost.set(host(url), url)

    if (byHost.size) {
        const line = Div()
        line.addClass(style.sourceList)

        const label = Div('منابع:')
        label.addClass(style.sourceLabel)
        line.append(label)

        for (const [name, url] of byHost) {
            // _blank plus rel=noopener: on native this hands the address to the
            // system browser rather than replacing the app's own page.
            const link = A(url, name, '_blank')
            link.addClass(style.sourceLink)
            link.el.rel = 'noopener'
            link.attr('dir', 'ltr')
            line.append(link)
        }
        base.append(line)
    }

    // The newest verification anywhere in this country's data: the date the page
    // is honestly current to.
    const checked = [country.verifiedAt, ...occasions.map(o => o.verifiedAt)]
        .filter((d): d is string => !!d)
        .sort()
        .pop()
    if (checked) {
        const when = Div(`آخرین بازبینی: ${faJalaali(checked)}`)
        when.addClass(style.checked)
        base.append(when)
    }

    // Cheapest possible correction loop, and it is what makes the sentence above
    // an offer rather than a disclaimer.
    const subject = `اصلاح تعطیلات ${country.names.fa ?? country.names.en} (${country.code})`
    const report = A(`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`,
        'گزارش اشتباه', '_self')
    report.addClass(style.report)
    base.append(report)

    return base
}
